import * as React from 'react'
import { InjectedFormProps, reduxForm, Field } from 'redux-form'
import { useSelector, useDispatch } from 'react-redux'
import { RouteComponentProps } from 'react-router'
import * as _ from 'lodash'
import { translate } from '../../i18n'
import { IAppState } from '../../store/state.interface.web'
import { FormField } from '../../../Web/components/FormField/FormField'
import { showErrorMessage } from '../../utils'
import { verifyInviteToken, acceptInvite, acceptOrgInvite } from './acceptInvite.reducer'
import { AcceptInviteForm } from './constants'
import { IUser } from '../../users/index.interface'
import { required } from '../../../Web/common/validate'
import LogoHeader from '../logoHeader'

interface ILayoutProps {
  inviteToken?: string
}

interface IComponentProps extends RouteComponentProps<ILayoutProps> {
}

const AcceptInviteFormView: React.FC<
  InjectedFormProps<{}, IComponentProps> & IComponentProps
> = props => {
  // * varaiable declarations
  const { handleSubmit } = props
  const { useEffect } = React
  const token = props.match.params.inviteToken!

  const [verificationInProgres, submitting] = useSelector<IAppState, [boolean, boolean]>((state: IAppState) =>
    [state.acceptInvite.verificationInProgress, state.acceptInvite.submitting])

  const user = useSelector<IAppState, Partial<IUser>>((state: IAppState) => state.acceptInvite.user)

  const dispatch = useDispatch()

  const acceptInviteFormSubmit = (submittedValues: any) => {
    if (submittedValues.password !== submittedValues.confirmPassword) {
      return showErrorMessage(
        `Password doesn't match! Enter your Password Again`
      )
    }
    submittedValues = Object.assign({}, submittedValues, { token })
    if (user && user.organization && user.organization.status === 'INVITED') {
      dispatch(acceptOrgInvite(submittedValues))
    } else {
      dispatch(acceptInvite(submittedValues))
    }
  }

  useEffect(() => {
    dispatch(verifyInviteToken({ token }))
  }, [token])

  useEffect(() => {
    if (user) {
      props.initialize({
        ...user,
      })
    }
  }, [user])

  const renderLoader = () => {
    if (submitting) {
      return <em className="fas fa-circle-notch fa-spin ml-1" />
    }
  }

  return (
    <>
      <div className="block-center wd-xxxl d-flex flex-column full-height justify-content-center">
        <div className="card card-flat">
          <LogoHeader />
          <div className="card-body">
            <p className="text-center py-2 auth-title mainHeadingColor">
              {translate('acceptInvite.acceptInvitationHeading')}
            </p>
            {verificationInProgres && <p className="text-center">
              {translate('acceptInvite.verificationInProgress')}
            </p>
            }
            <form>
              {
                !_.isEmpty(user) && user.organization && user.organization.status === 'INVITED' ? (
                  <div className="mb-4">
                    <h6 className="subHeading">
                      {translate(
                        'acceptInvite.organzitionHeading'
                      )}
                    </h6>
                    <p className="practice-subtitle">
                      {translate(
                        'acceptInvite.organizationSubheading'
                      )}
                    </p>
                    <Field
                      label={translate(
                        'practices.createPracticeComponent.orgNameLabel'
                      )}
                      type="text"
                      name="organization.name"
                      required
                      component={FormField}
                      validate={[required]}
                      placeholder={translate(
                        'practices.createPracticeComponent.orgNamePlaceHolder'
                      )}
                    />
                    <Field
                      label={translate(
                        'practices.createPracticeComponent.orgWebsiteLabel'
                      )}
                      type="text"
                      name="organization.website"
                      component={FormField}
                      placeholder={translate(
                        'practices.createPracticeComponent.orgWebsitePlaceHolder'
                      )}
                    />
                    <Field
                      label={translate(
                        'practices.createPracticeComponent.orgPhoneLabel'
                      )}
                      validate={[required]}
                      required
                      type="phonenumber"
                      name="organization.phoneNumber"
                      component={FormField}
                      placeholder={translate(
                        'practices.createPracticeComponent.orgPhonePlaceHolder'
                      )}
                    />
                    <Field
                      label={translate(
                        'practices.createPracticeComponent.addressLabel'
                      )}
                      type="text"
                      name="organization.addrSt1"
                      validate={[required]}
                      required
                      component={FormField}
                      placeholder={translate(
                        'practices.createPracticeComponent.addressPlaceHolder'
                      )}
                    />
                    <Field
                      type="text"
                      name="organization.addrSt2"
                      component={FormField}
                      placeholder={translate(
                        'practices.createPracticeComponent.street2PlaceHolder'
                      )}
                    />
                    <Field
                      type="text"
                      name="organization.addrCity"
                      component={FormField}
                      placeholder={translate(
                        'practices.createPracticeComponent.cityPlaceHolder'
                      )}
                    />
                    <Field
                      type="text"
                      name="organization.addrState"
                      component={FormField}
                      placeholder={translate(
                        'practices.createPracticeComponent.statePlaceHolder'
                      )}
                    />
                    <Field
                      type="text"
                      name="organization.addrZip"
                      component={FormField}
                      placeholder={translate(
                        'practices.createPracticeComponent.zipcodePlaceHolder'
                      )}
                    />

                  </div>
                ) : null
              }
              {
                !_.isEmpty(user) ?
                  (<div className="mb-4">
                    <h6 className="subHeading">
                      {translate(
                        'acceptInvite.PersonalInformationHeading'
                      )}
                    </h6>
                    <p className="practice-subtitle">
                      {translate(
                        'acceptInvite.PersonalInformationSubheading'
                      )}
                    </p>
                    <Field
                      name="firstName"
                      component={FormField}
                      type="text"
                      placeholder={translate(
                        'user.firstNameTable'
                      )}
                      required
                      validate={[required]}
                      label={translate(
                        'users.editUserComponent.firstNameLabel'
                      )}
                    />
                    <Field
                      name="lastName"
                      component={FormField}
                      type="text"
                      placeholder={translate(
                        'user.lastNameTable'
                      )}
                      required
                      validate={[required]}
                      label={translate(
                        'users.editUserComponent.lastNameLabel'
                      )}
                    />
                    <Field
                      name="npi"
                      component={FormField}
                      type="text"
                      placeholder={translate(
                        'practices.npiLabel'
                      )}
                      label={translate(
                        'users.editUserComponent.npiLabel'
                      )}
                    />
                    <Field
                      name="title"
                      component={FormField}
                      required
                      type="text"
                      placeholder={translate(
                        'acceptInvite.titlePlaceholder'
                      )}
                      validate={[required]}
                      label={translate(
                        'users.editUserComponent.titleLabel'
                      )}
                    />
                  </div>
                  )
                  : null
              }
              {!_.isEmpty(user) ?
                (<>
                  <h6 className="subHeading">
                    {translate(
                      'acceptInvite.passwordHeading'
                    )}
                  </h6>
                  <Field
                    name="password"
                    component={FormField}
                    type="password"
                    placeholder={translate(
                      'forgotPassword.setNewPassword.passwordPlaceholder'
                    )}
                    validate={[required]}
                    label={`${translate('acceptInvite.password')}`}
                  />{' '}
                  <Field
                    name="confirmPassword"
                    component={FormField}
                    type="password"
                    placeholder={translate(
                      'forgotPassword.setNewPassword.confirmPasswordPlaceholder'
                    )}
                    validate={[required]}
                    label={`${translate('acceptInvite.confirmPassword')}`}
                  />
                  <button
                    className="btn btn-primary btn-block"
                    type="button"
                    onClick={handleSubmit(acceptInviteFormSubmit)}
                  >
                    {translate('forgotPassword.setNewPassword.acceptLabel')} {renderLoader()}
                  </button>
                </>) : null
              }
            </form>
          </div>
        </div>
        <div className="p-3 text-center">
          <span className="mr-2">&copy;</span>
          <span>{translate('authLayout.copyRights')}</span>
          <span className="mx-2"></span>
          <br />
          <span>{`Remote Patient Monitoring`}</span>
          <br />
          <span>{translate('authLayout.rightsReserved')}</span>
        </div>
      </div>
    </>
  )
}

export default reduxForm<{}, RouteComponentProps>({
  form: AcceptInviteForm,
  keepDirtyOnReinitialize: true
})(AcceptInviteFormView)
